'use client';
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

export interface BeforeAfterItem {
  id: string;
  label: string;
  package: string;
  before: string;
  after: string;
  beforeAlt: string;
  afterAlt: string;
  stat: string;
  sort_order?: number;
}

export interface ReviewItem {
  id: string;
  name: string;
  location: string;
  rating: number;
  text: string;
  package: string;
  date: string;
  sort_order?: number;
  is_approved?: boolean;
  email?: string;
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(0,245,255,0.12)',
  color: '#F5F5F7',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: 14,
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'rgba(168,169,173,0.5)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 6,
  fontFamily: 'monospace',
};

const cardStyle: React.CSSProperties = {
  background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(0,245,255,0.04) 100%)',
  border: '1px solid rgba(0,245,255,0.1)',
  borderRadius: 16,
  padding: '20px',
};

const emptyPhoto: Omit<BeforeAfterItem, 'id'> = {
  label: '',
  package: '',
  before: '',
  after: '',
  beforeAlt: '',
  afterAlt: '',
  stat: '',
};

const emptyReview: Omit<ReviewItem, 'id'> = {
  name: '',
  location: '',
  rating: 5,
  text: '',
  package: '',
  date: 'Early Access Member',
};

// ── Photo Form ───────────────────────────────────────────────────────────────
interface PhotoFormProps {
  initial?: BeforeAfterItem;
  onSave: (item: BeforeAfterItem) => void;
  onCancel: () => void;
}

function PhotoForm({ initial, onSave, onCancel }: PhotoFormProps) {
  const [form, setForm] = useState<Omit<BeforeAfterItem, 'id'>>(
    initial
      ? { label: initial.label, package: initial.package, before: initial.before, after: initial.after, beforeAlt: initial.beforeAlt, afterAlt: initial.afterAlt, stat: initial.stat }
      : { ...emptyPhoto }
  );

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.label.trim() || !form.before.trim() || !form.after.trim()) return;
    onSave({ ...form, id: initial?.id ?? '' });
  };

  return (
    <div style={{ ...cardStyle, border: '1px solid rgba(0,245,255,0.25)' }} className="space-y-4">
      <p className="mono text-[11px] tracking-widest uppercase" style={{ color: '#00f5ff' }}>
        {initial ? '✎ Edit Photo Pair' : '+ New Before / After Pair'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label style={labelStyle}>Label *</label>
          <input style={inputStyle} value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Full Interior Detail" />
        </div>
        <div>
          <label style={labelStyle}>Package</label>
          <input style={inputStyle} value={form.package} onChange={(e) => set('package', e.target.value)} placeholder="e.g. Premium Bundle" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label style={labelStyle}>Before Image URL *</label>
          <input style={inputStyle} value={form.before} onChange={(e) => set('before', e.target.value)} placeholder="https://..." />
          {form.before && (
            <img src={form.before} alt="before preview" style={{ marginTop: 8, width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,0,255,0.3)' }} />
          )}
        </div>
        <div>
          <label style={labelStyle}>After Image URL *</label>
          <input style={inputStyle} value={form.after} onChange={(e) => set('after', e.target.value)} placeholder="https://..." />
          {form.after && (
            <img src={form.after} alt="after preview" style={{ marginTop: 8, width: '100%', height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(0,245,255,0.3)' }} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label style={labelStyle}>Before Alt Text</label>
          <input style={inputStyle} value={form.beforeAlt} onChange={(e) => set('beforeAlt', e.target.value)} placeholder="Describe the before image" />
        </div>
        <div>
          <label style={labelStyle}>After Alt Text</label>
          <input style={inputStyle} value={form.afterAlt} onChange={(e) => set('afterAlt', e.target.value)} placeholder="Describe the after image" />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Stat Badge (e.g. "4h service")</label>
        <input style={{ ...inputStyle, width: 200 }} value={form.stat} onChange={(e) => set('stat', e.target.value)} placeholder="4h service" />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={!form.label.trim() || !form.before.trim() || !form.after.trim()}
          className="px-5 py-2.5 rounded-xl mono text-[10px] font-semibold tracking-widest uppercase transition-all disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, rgba(0,245,255,0.2) 0%, rgba(123,47,255,0.2) 100%)', border: '1px solid rgba(0,245,255,0.35)', color: '#00f5ff', cursor: 'pointer' }}
        >
          ✓ Save Pair
        </button>
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-xl mono text-[10px] font-semibold tracking-widest uppercase transition-all"
          style={{ background: 'transparent', border: '1px solid rgba(168,169,173,0.2)', color: 'rgba(168,169,173,0.5)', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Review Form ──────────────────────────────────────────────────────────────
interface ReviewFormProps {
  initial?: ReviewItem;
  onSave: (item: ReviewItem) => void;
  onCancel: () => void;
}

function ReviewForm({ initial, onSave, onCancel }: ReviewFormProps) {
  const [form, setForm] = useState<Omit<ReviewItem, 'id'>>(
    initial
      ? { name: initial.name, location: initial.location, rating: initial.rating, text: initial.text, package: initial.package, date: initial.date }
      : { ...emptyReview }
  );

  const set = (k: keyof typeof form, v: string | number) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim() || !form.text.trim()) return;
    onSave({ ...form, id: initial?.id ?? '' });
  };

  return (
    <div style={{ ...cardStyle, border: '1px solid rgba(123,47,255,0.35)' }} className="space-y-4">
      <p className="mono text-[11px] tracking-widest uppercase" style={{ color: '#7b2fff' }}>
        {initial ? '✎ Edit Review' : '+ New Customer Review'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label style={labelStyle}>Customer Name *</label>
          <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Marcus T." />
        </div>
        <div>
          <label style={labelStyle}>Location</label>
          <input style={inputStyle} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Las Vegas, NV" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label style={labelStyle}>Package / Service</label>
          <input style={inputStyle} value={form.package} onChange={(e) => set('package', e.target.value)} placeholder="e.g. Luxe Package" />
        </div>
        <div>
          <label style={labelStyle}>Date / Label</label>
          <input style={inputStyle} value={form.date} onChange={(e) => set('date', e.target.value)} placeholder="e.g. Early Access Member" />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Star Rating</label>
        <div className="flex gap-2 mt-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => set('rating', star)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 24,
                color: star <= form.rating ? '#00f5ff' : 'rgba(168,169,173,0.2)',
                textShadow: star <= form.rating ? '0 0 8px rgba(0,245,255,0.6)' : 'none',
                transition: 'all 0.15s ease',
                padding: '2px 4px',
              }}
            >
              ★
            </button>
          ))}
          <span className="mono text-[11px] self-center ml-1" style={{ color: 'rgba(168,169,173,0.5)' }}>{form.rating}/5</span>
        </div>
      </div>

      <div>
        <label style={labelStyle}>Review Text *</label>
        <textarea
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
          value={form.text}
          onChange={(e) => set('text', e.target.value)}
          placeholder="Customer's review text..."
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={!form.name.trim() || !form.text.trim()}
          className="px-5 py-2.5 rounded-xl mono text-[10px] font-semibold tracking-widest uppercase transition-all disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, rgba(123,47,255,0.25) 0%, rgba(255,0,255,0.15) 100%)', border: '1px solid rgba(123,47,255,0.4)', color: '#7b2fff', cursor: 'pointer' }}
        >
          ✓ Save Review
        </button>
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-xl mono text-[10px] font-semibold tracking-widest uppercase transition-all"
          style={{ background: 'transparent', border: '1px solid rgba(168,169,173,0.2)', color: 'rgba(168,169,173,0.5)', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Helpers to map DB rows ↔ component types ─────────────────────────────────
function dbRowToPhoto(row: Record<string, unknown>): BeforeAfterItem {
  return {
    id: row.id as string,
    label: (row.label as string) ?? '',
    package: (row.package as string) ?? '',
    before: (row.before_url as string) ?? '',
    after: (row.after_url as string) ?? '',
    beforeAlt: (row.before_alt as string) ?? '',
    afterAlt: (row.after_alt as string) ?? '',
    stat: (row.stat as string) ?? '',
    sort_order: (row.sort_order as number) ?? 0,
  };
}

function dbRowToReview(row: Record<string, unknown>): ReviewItem {
  return {
    id: row.id as string,
    name: (row.name as string) ?? '',
    location: (row.location as string) ?? '',
    rating: (row.rating as number) ?? 5,
    text: (row.review_text as string) ?? '',
    package: (row.package as string) ?? '',
    date: (row.date_label as string) ?? 'Early Access Member',
    sort_order: (row.sort_order as number) ?? 0,
    is_approved: (row.is_approved as boolean) ?? false,
    email: (row.email as string) ?? '',
  };
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function ReviewsManager() {
  const [subTab, setSubTab] = useState<'photos' | 'reviews'>('photos');
  const [photos, setPhotos] = useState<BeforeAfterItem[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [editingPhoto, setEditingPhoto] = useState<BeforeAfterItem | null>(null);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [editingReview, setEditingReview] = useState<ReviewItem | null>(null);
  const [addingReview, setAddingReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashSaved = () => {
    setSaved(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 2500);
  };

  // ── Load from Supabase ──
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [photosRes, reviewsRes] = await Promise.all([
          supabase.from('before_after_photos').select('*').order('sort_order', { ascending: true }),
          supabase.from('reviews').select('*').order('sort_order', { ascending: true }),
        ]);
        if (photosRes.error) throw photosRes.error;
        if (reviewsRes.error) throw reviewsRes.error;
        setPhotos((photosRes.data ?? []).map(dbRowToPhoto));
        setReviews((reviewsRes.data ?? []).map(dbRowToReview));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Photos CRUD ──
  const savePhoto = async (item: BeforeAfterItem) => {
    setError(null);
    try {
      const payload = {
        label: item.label,
        package: item.package,
        before_url: item.before,
        after_url: item.after,
        before_alt: item.beforeAlt,
        after_alt: item.afterAlt,
        stat: item.stat,
        sort_order: item.sort_order ?? photos.length,
      };

      if (editingPhoto && item.id) {
        const { data, error: err } = await supabase
          .from('before_after_photos')
          .update(payload)
          .eq('id', item.id)
          .select()
          .single();
        if (err) throw err;
        setPhotos((prev) => prev.map((p) => (p.id === item.id ? dbRowToPhoto(data) : p)));
      } else {
        const { data, error: err } = await supabase
          .from('before_after_photos')
          .insert({ ...payload, sort_order: photos.length })
          .select()
          .single();
        if (err) throw err;
        setPhotos((prev) => [...prev, dbRowToPhoto(data)]);
      }
      flashSaved();
      setEditingPhoto(null);
      setAddingPhoto(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save photo');
    }
  };

  const deletePhoto = async (id: string) => {
    setError(null);
    try {
      const { error: err } = await supabase.from('before_after_photos').delete().eq('id', id);
      if (err) throw err;
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      flashSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    }
  };

  const movePhoto = async (id: string, dir: -1 | 1) => {
    const idx = photos.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= photos.length) return;
    const updated = [...photos];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    const reordered = updated.map((p, i) => ({ ...p, sort_order: i }));
    setPhotos(reordered);
    try {
      await Promise.all(
        reordered.map((p) =>
          supabase.from('before_after_photos').update({ sort_order: p.sort_order }).eq('id', p.id)
        )
      );
      flashSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reorder photos');
    }
  };

  // ── Reviews CRUD ──
  const saveReview = async (item: ReviewItem) => {
    setError(null);
    try {
      const payload = {
        name: item.name,
        location: item.location,
        rating: item.rating,
        review_text: item.text,
        package: item.package,
        date_label: item.date,
        sort_order: item.sort_order ?? reviews.length,
      };

      if (editingReview && item.id) {
        const { data, error: err } = await supabase
          .from('reviews')
          .update(payload)
          .eq('id', item.id)
          .select()
          .single();
        if (err) throw err;
        setReviews((prev) => prev.map((r) => (r.id === item.id ? dbRowToReview(data) : r)));
      } else {
        const { data, error: err } = await supabase
          .from('reviews')
          .insert({ ...payload, sort_order: reviews.length })
          .select()
          .single();
        if (err) throw err;
        setReviews((prev) => [...prev, dbRowToReview(data)]);
      }
      flashSaved();
      setEditingReview(null);
      setAddingReview(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save review');
    }
  };

  const deleteReview = async (id: string) => {
    setError(null);
    try {
      const { error: err } = await supabase.from('reviews').delete().eq('id', id);
      if (err) throw err;
      setReviews((prev) => prev.filter((r) => r.id !== id));
      flashSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete review');
    }
  };

  const moveReview = async (id: string, dir: -1 | 1) => {
    const idx = reviews.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= reviews.length) return;
    const updated = [...reviews];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    const reordered = updated.map((r, i) => ({ ...r, sort_order: i }));
    setReviews(reordered);
    try {
      await Promise.all(
        reordered.map((r) =>
          supabase.from('reviews').update({ sort_order: r.sort_order }).eq('id', r.id)
        )
      );
      flashSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reorder reviews');
    }
  };

  const toggleApprove = async (id: string, current: boolean) => {
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('reviews')
        .update({ is_approved: !current })
        .eq('id', id)
        .select()
        .single();
      if (err) throw err;
      setReviews((prev) => prev.map((r) => (r.id === id ? dbRowToReview(data) : r)));
      flashSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update approval');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="mono text-[10px] tracking-widest uppercase mb-1" style={{ color: 'rgba(168,169,173,0.45)' }}>
            Results & Reviews Popup
          </p>
          <h2 className="text-lg font-bold tracking-tight" style={{ color: '#F5F5F7' }}>
            Content Manager
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.25)', color: '#00f5ff' }}>
              ✓ Saved
            </span>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.3)' }}>
          <p className="text-xs" style={{ color: 'rgba(220,53,69,0.9)' }}>⚠ {error}</p>
        </div>
      )}

      {/* Info banner */}
      <div className="rounded-xl p-4" style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.1)' }}>
        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
          📸 Changes here are saved to <strong style={{ color: '#00f5ff' }}>Supabase</strong> and instantly reflected in the <strong style={{ color: '#00f5ff' }}>Results & Reviews</strong> slide-out popup on the home page.
        </p>
      </div>

      {/* Sub-tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl w-fit"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,169,173,0.08)' }}
      >
        {(['photos', 'reviews'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            className="px-4 py-2 rounded-lg mono text-[10px] font-semibold tracking-widest uppercase transition-all duration-200"
            style={
              subTab === t
                ? {
                    background: t === 'photos' ? 'linear-gradient(135deg, rgba(0,245,255,0.15) 0%, rgba(123,47,255,0.15) 100%)' : 'linear-gradient(135deg, rgba(123,47,255,0.2) 0%, rgba(255,0,255,0.1) 100%)',
                    border: t === 'photos' ? '1px solid rgba(0,245,255,0.3)' : '1px solid rgba(123,47,255,0.4)',
                    color: t === 'photos' ? '#00f5ff' : '#7b2fff',
                  }
                : {
                    background: 'transparent',
                    border: '1px solid transparent',
                    color: 'rgba(168,169,173,0.45)',
                  }
            }
          >
            {t === 'photos' ? `◈ Before / After (${photos.length})` : `★ Reviews (${reviews.length})`}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <span className="mono text-[11px] tracking-widest uppercase" style={{ color: 'rgba(0,245,255,0.5)' }}>
            ◌ Loading from Supabase…
          </span>
        </div>
      )}

      {/* ── Photos Tab ── */}
      {!loading && subTab === 'photos' && (
        <div className="space-y-4">
          {addingPhoto && (
            <PhotoForm onSave={savePhoto} onCancel={() => setAddingPhoto(false)} />
          )}

          {photos.map((photo, idx) => (
            <div key={photo.id}>
              {editingPhoto?.id === photo.id ? (
                <PhotoForm initial={photo} onSave={savePhoto} onCancel={() => setEditingPhoto(null)} />
              ) : (
                <div style={cardStyle} className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex gap-2 shrink-0">
                    <div style={{ position: 'relative' }}>
                      <img src={photo.before} alt={photo.beforeAlt} style={{ width: 72, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,0,255,0.3)' }} />
                      <span style={{ position: 'absolute', bottom: 2, left: 2, background: 'rgba(5,5,16,0.85)', border: '1px solid rgba(255,0,255,0.4)', padding: '1px 5px', fontFamily: 'monospace', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#ff00ff' }}>Before</span>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <img src={photo.after} alt={photo.afterAlt} style={{ width: 72, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(0,245,255,0.3)' }} />
                      <span style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(5,5,16,0.85)', border: '1px solid rgba(0,245,255,0.4)', padding: '1px 5px', fontFamily: 'monospace', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#00f5ff' }}>After</span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm mb-0.5" style={{ color: '#e0e8ff' }}>{photo.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {photo.package && (
                        <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(123,47,255,0.12)', border: '1px solid rgba(123,47,255,0.25)', color: '#7b2fff' }}>{photo.package}</span>
                      )}
                      {photo.stat && (
                        <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff' }}>{photo.stat}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => movePhoto(photo.id, -1)} disabled={idx === 0} title="Move up" className="w-8 h-8 rounded-lg flex items-center justify-center mono text-sm transition-all disabled:opacity-25" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,169,173,0.12)', color: 'rgba(168,169,173,0.6)', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>↑</button>
                    <button onClick={() => movePhoto(photo.id, 1)} disabled={idx === photos.length - 1} title="Move down" className="w-8 h-8 rounded-lg flex items-center justify-center mono text-sm transition-all disabled:opacity-25" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,169,173,0.12)', color: 'rgba(168,169,173,0.6)', cursor: idx === photos.length - 1 ? 'not-allowed' : 'pointer' }}>↓</button>
                    <button onClick={() => { setEditingPhoto(photo); setAddingPhoto(false); }} className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all" style={{ background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.2)', color: '#00f5ff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deletePhoto(photo.id)} className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all" style={{ background: 'rgba(220,53,69,0.06)', border: '1px solid rgba(220,53,69,0.2)', color: 'rgba(220,53,69,0.7)', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {!addingPhoto && (
            <button
              onClick={() => { setAddingPhoto(true); setEditingPhoto(null); }}
              className="w-full py-4 rounded-xl mono text-[11px] font-semibold tracking-widest uppercase transition-all"
              style={{ background: 'rgba(0,245,255,0.03)', border: '1px dashed rgba(0,245,255,0.2)', color: 'rgba(0,245,255,0.5)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,245,255,0.5)'; (e.currentTarget as HTMLButtonElement).style.color = '#00f5ff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,245,255,0.2)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(0,245,255,0.5)'; }}
            >
              + Add Before / After Pair
            </button>
          )}
        </div>
      )}

      {/* ── Reviews Tab ── */}
      {!loading && subTab === 'reviews' && (
        <div className="space-y-4">
          {addingReview && (
            <ReviewForm onSave={saveReview} onCancel={() => setAddingReview(false)} />
          )}

          {reviews.map((review, idx) => (
            <div key={review.id}>
              {editingReview?.id === review.id ? (
                <ReviewForm initial={review} onSave={saveReview} onCancel={() => setEditingReview(null)} />
              ) : (
                <div style={cardStyle} className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="shrink-0 flex flex-col items-center gap-1 pt-1">
                    <div className="flex gap-0.5">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <span key={i} style={{ color: '#00f5ff', fontSize: 14, textShadow: '0 0 6px rgba(0,245,255,0.5)' }}>★</span>
                      ))}
                    </div>
                    <span className="mono text-[9px] tracking-widest" style={{ color: 'rgba(168,169,173,0.4)' }}>{review.rating}/5</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-semibold text-sm" style={{ color: '#e0e8ff' }}>{review.name}</span>
                      {review.location && <span className="mono text-[10px]" style={{ color: 'rgba(168,169,173,0.5)' }}>📍 {review.location}</span>}
                    </div>
                    <p className="text-xs leading-relaxed mb-2" style={{ color: '#a0b0d8', fontStyle: 'italic' }}>"{review.text}"</p>
                    <div className="flex flex-wrap gap-2">
                      {review.package && (
                        <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(123,47,255,0.12)', border: '1px solid rgba(123,47,255,0.25)', color: '#7b2fff' }}>{review.package}</span>
                      )}
                      {review.date && (
                        <span className="mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.15)', color: 'rgba(0,245,255,0.6)' }}>{review.date}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => toggleApprove(review.id, review.is_approved ?? false)}
                      title={review.is_approved ? 'Revoke approval' : 'Approve for public display'}
                      className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all"
                      style={
                        review.is_approved
                          ? { background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.4)', color: '#00f5ff', cursor: 'pointer' }
                          : { background: 'rgba(255,165,0,0.06)', border: '1px solid rgba(255,165,0,0.3)', color: 'rgba(255,165,0,0.8)', cursor: 'pointer' }
                      }
                    >
                      {review.is_approved ? '✓ Live' : '⏳ Pending'}
                    </button>
                    <button onClick={() => moveReview(review.id, -1)} disabled={idx === 0} title="Move up" className="w-8 h-8 rounded-lg flex items-center justify-center mono text-sm transition-all disabled:opacity-25" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,169,173,0.12)', color: 'rgba(168,169,173,0.6)', cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>↑</button>
                    <button onClick={() => moveReview(review.id, 1)} disabled={idx === reviews.length - 1} title="Move down" className="w-8 h-8 rounded-lg flex items-center justify-center mono text-sm transition-all disabled:opacity-25" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,169,173,0.12)', color: 'rgba(168,169,173,0.6)', cursor: idx === reviews.length - 1 ? 'not-allowed' : 'pointer' }}>↓</button>
                    <button onClick={() => { setEditingReview(review); setAddingReview(false); }} className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all" style={{ background: 'rgba(123,47,255,0.08)', border: '1px solid rgba(123,47,255,0.25)', color: '#7b2fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deleteReview(review.id)} className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all" style={{ background: 'rgba(220,53,69,0.06)', border: '1px solid rgba(220,53,69,0.2)', color: 'rgba(220,53,69,0.7)', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {!addingReview && (
            <button
              onClick={() => { setAddingReview(true); setEditingReview(null); }}
              className="w-full py-4 rounded-xl mono text-[11px] font-semibold tracking-widest uppercase transition-all"
              style={{ background: 'rgba(123,47,255,0.03)', border: '1px dashed rgba(123,47,255,0.25)', color: 'rgba(123,47,255,0.55)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(123,47,255,0.5)'; (e.currentTarget as HTMLButtonElement).style.color = '#7b2fff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(123,47,255,0.25)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(123,47,255,0.55)'; }}
            >
              + Add Customer Review
            </button>
          )}
        </div>
      )}
    </div>
  );
}
